import { useState, useEffect, useRef, useCallback } from "react";

import { LoadingOutlined } from "@ant-design/icons";
import { Popover, Tooltip } from "antd";
import { motion } from "framer-motion";
import Interweave from "interweave";
import moment from "moment";
import { useRouter } from "next/dist/client/router";
import { useDispatch, useSelector } from "react-redux";
import {
  WhatsappShareButton,
  WhatsappIcon,
  EmailIcon,
  EmailShareButton,
} from "react-share";
import { toast } from "react-toastify";

import BandComponent from "./BandComponent";
import styles from "./BandConfiguration.module.css";
import ExtraComponent from "./ExtraComponent";
import SegmentComponent from "./SegmentComponent";
import StringsComponent from "./StringsComponent";
import TabContent from "./TabContent";
import tabs from "./tabs";
import TravelComponent from "./TravelComponent";
import { ReactToastify, CircularProgress } from "../../../helper/ReusableComponents";
import {
  formatToCurrency,
  getTotalMembers,
  getTotalPerformers,
  updateObject,
  applyMargin, 
  SegmentOperations as SegOp
} from "../../../helper/service";
import { onLogout } from "../../../store/actions";
import {
  setTravelPriceAndDistance,
  SearchAction,
} from "../../../store/actions/searchAction";
import { AxiosInstance, logout } from "../../../utilities";

const BandConfiguration = ({
  bandId,
  onBandTotalChange,
  onBandPiecesChange,
  onSaveClick,
  slug,
  cmsData,
}) => {
  const auth = useSelector((state) => state.auth);
  const reduxState = useSelector((state) => state);
  const dispatch = useDispatch();
  const router = useRouter();

  const [tabsData, setTabsData] = useState(tabs);
  const [completedTabs, setCompletedTabs] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalDiscountPrice, setTotalDiscountPrice] = useState(0);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [bandConfigData, setBandConfigData] = useState([]);
  const [bandDataLoading, setBandDataLoading] = useState(true);
  const [extraData, setExtraData] = useState([]);
  const [margin, setMargin] = useState(0);
  const [bandDataError, setBandDataError] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [faqData, setFaqData] = useState();

  const [notes, setNotes] = useState("");
  const [discount, setDiscount] = useState("");
  const [discounted, setDiscounted] = useState(0);
  const [discountAccess, setDiscountAccess] = useState("");
  const [travelResponse, setTravelResponse] = useState(
    reduxState?.search?.travelPrices || []
  );
  const [travelDistance, setTravelDistance] = useState(
    reduxState?.search?.distance || 0
  );

  const [travelData, setTravelData] = useState([]);
  const [travelErrorMsg, setTravelErrorMsg] = useState("");
  const [travelDataLoading, setTravelDataLoading] = useState(true);
  const [stringsErrorMsg, setStringsErrorMsg] = useState("");

  const [isAuthenticated, setIsAuthenticate] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState("");
  const downloadTimerRef = useRef(null);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [bookNowLoading, setBookNowLoading] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [ccAdded, setCcAdded] = useState(false);
  const [travelRate, setTravelRate] = useState(0);
  const [disableActions, setDisableActions] = useState(false);
  const [creditCard, setCreditCard] = useState(0);
  const [commissionPercentage, setCommissionPercentage] = useState(0)

  // The deferred updateSegmentTotal calls below must always price the latest
  // team/segments, not the render they were scheduled from (STAR-1847: stale
  // totals dropped the price of the most recently added team member)
  const bandConfigDataRef = useRef(bandConfigData);
  bandConfigDataRef.current = bandConfigData;
  const segmentTotalTimerRef = useRef(null);

  const startProgressPolling = useCallback((eventId) => {
    setDownloadProgress(0);
    setDownloadStatus("Saving proposal...");
    downloadTimerRef.current = setInterval(() => {
      AxiosInstance(4009, "application/json")
        .get(`/proposalProgress/${eventId}`)
        .then((res) => {
          const { progress, status } = res.data || {};
          if (progress) setDownloadProgress(progress);
          if (status) setDownloadStatus(status);
        })
        .catch(() => {});
    }, 500);
  }, []);

  const stopProgressPolling = useCallback(() => {
    if (downloadTimerRef.current) {
      clearInterval(downloadTimerRef.current);
      downloadTimerRef.current = null;
    }
  }, []);

  const completeDownloadProgress = useCallback(() => {
    stopProgressPolling();
    setDownloadProgress(100);
    setDownloadStatus("Complete");
    // Hold at 100% for 1s so the full circle is visible, then fade back to icon
    setTimeout(() => {
      setDownloadLoading(false);
      // Reset progress after the fade-back animation (0.6s) completes
      setTimeout(() => {
        setDownloadProgress(0);
        setDownloadStatus("");
      }, 700);
    }, 1000);
  }, [stopProgressPolling]);

  const resetDownloadProgress = useCallback(() => {
    stopProgressPolling();
    setDownloadLoading(false);
    setDownloadProgress(0);
    setDownloadStatus("");
  }, [stopProgressPolling]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopProgressPolling();
  }, [stopProgressPolling]);

  useEffect(() => {
    if (auth && auth.token) {
      setIsAuthenticate(true);
    } else {
      setIsAuthenticate(false);
    }
  }, [auth]);
  
  useEffect(async () => {
    setDisableActions(false);
    const { date, eventType, latitude, longitude, event_place } = router?.query;

    let results
    reduxState?.search?.search_result?.map((searchResult)=>{
      if(searchResult.slug === router?.asPath.split("/")[2]){
        results=searchResult.slug
      }})
      // console.log(results,"SS");
      // console.log(router?.asPath.split("/")[2],"TT");
      // console.log(reduxState?.search?.search_result,"UU");
    if(router?.asPath.split("/")[2] !== results &&  reduxState?.search?.search_result !== null){
      let searchMsg = "This Band is already booked for this date please try another date";
      setBandDataError(searchMsg);
      setTravelErrorMsg(searchMsg);
      setTravelDataLoading(false);
      setDisableActions(true);
    }
    else if (reduxState.search?.searchData?.eventType !== undefined) {
      bandId && fetchBandConfiguration(bandId, reduxState.search?.searchData);
      // fetchTravelDetails(reduxState.search?.searchData);
    } else if (!!date && !!eventType && !!latitude && !!longitude) {
      const searchData = {
        date: date,
        eventType: eventType,
        latitude: Number(latitude),
        longitude: Number(longitude),
        event_place: event_place,
        page:1
      };
      dispatch(SearchAction(searchData));
      bandId && fetchBandConfiguration(bandId, router.query);
      // fetchTravelDetails(router.query);
    } else {
      let searchMsg = "Complete the Search Form above to configure this band";
      setBandDataError(searchMsg);
      setTravelErrorMsg(searchMsg);
      setTravelDataLoading(false);
    }
    fetchFaqDetails();
  }, [bandId,router]);

  // Recalculate the overtime price of performer
  useEffect(() => {
    if (bandConfigData && Object.keys(bandConfigData).length > 0) {
      bandConfigData.segments.forEach((segment, objIdx) =>
        calculateOverTime(bandConfigData.segments, objIdx, segment)
      );

      //update total piece count in hero slider
      const respn = bandConfigData.segments.find(
        (item) => item.name === "Reception"
      );
      onBandPiecesChange(respn.musicians);
    }
  }, [bandConfigData.segments, margin]);

  useEffect(() => {
    const total = tabsData.reduce((total, tab) => total + tab.price, 0);
    if(discounted > 0){
      setTotalDiscountPrice(Math.round(total) - discounted);
      onBandTotalChange(Math.round(total) - discounted);
      setTotalPrice(Math.round(total));
    }else{
      setTotalPrice(Math.round(total));
      onBandTotalChange(Math.round(total));
    }
    
  }, [/* bandConfigData.segments, */ bandConfigData.team, extraData, tabsData, discounted])

  useEffect(() => {
    if (bandConfigData && Object.keys(bandConfigData).length > 0) {
      bandConfigData.segments.forEach((segment, objIdx) =>{
        if([1, true].includes(segment.fullBand)) {
          calculateOverTime(bandConfigData.segments, objIdx, segment)
        }
      }
      );
    }
  },[ bandConfigData.team, totalPrice])

  const updateSegmentTotal = async (segmentArr) => {
    // always price the latest config — this runs from a setTimeout, so the
    // closure's bandConfigData can be a render (or several) behind
    const latestConfig = bandConfigDataRef.current ?? bandConfigData;
    const latestSegments = latestConfig?.segments ?? segmentArr;

    // total of overtime price from all segments
    const totalSegment = latestSegments.reduce((prev, cur) => {
      if (cur?.overtimePrice > 0) {
        return prev + Math.round(cur.overtimePrice);
      }
      return prev;
    }, 0);

    // calculate band price
    const totalPerformers = getTotalPerformers(latestConfig?.team);
    const totalMembers = getTotalMembers(latestConfig?.team, latestConfig?.haveSoundCompany);

    // get sum of margins of hotel and flight
    const FHTravelRate = getFHTravelRateMargin(travelResponse, totalMembers, totalPerformers);
    const FHTravelRateCC = calculateCreditCardCharge(FHTravelRate)

    let totalFHTravelRate = (FHTravelRate + FHTravelRateCC)
    const totalFHTravelCommission = calculateCommission(totalFHTravelRate)
    totalFHTravelRate = Math.round(totalFHTravelRate + totalFHTravelCommission)

    let totalBandPrice = latestConfig?.team?.reduce((sum, item) => {
      if([0,1,4,5,6].includes(item.type)) sum += _calculatePositionPrices({ position: item, segmentsClone: latestSegments })?.totalPrice ?? 0
      return sum
    },0)

    totalBandPrice += totalFHTravelRate
    setTabsData((prevTabs) => [...updateObject([...prevTabs], [
      {
        id: "segment",
        key: "price",
        value: Math.round(totalSegment),
      },
      {
        id: 'band',
        key: "price",
        value: totalBandPrice
      }
    ])]);
  };

  const calculateOverTime = (segmentArr, objIdx, segment) => {
    let startTime = moment(segmentArr[objIdx].startTime, "hh:mm a");
    let endTime = moment(segmentArr[objIdx].endTime, "hh:mm a");

    // get price of musicians for overtime
    let totalOverTimePrice = 0;
    let overtime = _getSegmentOvertime(startTime, endTime, segmentArr[objIdx].duration)

    if (overtime > 0 && segment.musicians > 0) {
      // console.info('Start')
      // const _tableLog = [];
      segment.performers.forEach((performer) => {
        const position = bandConfigData.team.find((obj) => obj.positionId === performer.positionId);

        let baseOTRate = 0;
        let payrollTax = 0;
        if (performer?.performerId) {
          const teamPerformer = position.performers.find((item) => item.performerId === performer.performerId)
          // const segmentOTRate = teamPerformer?.useDefaultRate  === false ? teamPerformer?.segmentRates?.find(s => s.segmentId === segment._id)?.rate : null;
          baseOTRate =  /*segmentOTRate ?? */(teamPerformer?.overtime ?? position.positionOvertime)
          if (teamPerformer?.w2Employee) payrollTax = baseOTRate
        } else {
          baseOTRate = position.positionOvertime
        }

        // baseOTRate *= overtime

        const baseOTRateWM = applyMargin(baseOTRate, margin)
        const payrollTaxWM = payrollTax ? calculatePayrollTax(baseOTRateWM) : 0
        const ccCharge = calculateCreditCardCharge(baseOTRateWM + payrollTaxWM)
        let finalPrice = (baseOTRateWM + ccCharge + payrollTaxWM);
        const commisionPrice = calculateCommission(finalPrice)

        finalPrice = /* Math.round */(finalPrice + commisionPrice);
        finalPrice = /* Math.round */SegOp.calcSegmentOTForDuration(finalPrice, overtime);
        // totalOverTimePrice += Math.round(finalPrice * 100) / 100;
        totalOverTimePrice += finalPrice

        // _tableLog.push({ segmentId: segmentArr[objIdx].segmentId, performerId: performer?.performerId, baseOTRateWM, payrollTaxWM, ccCharge, finalPrice })
      });
      // console.table(_tableLog)
      // console.info('end')
    }

    // update the overtime to segment table
    if (totalOverTimePrice > 0 && segmentArr[objIdx].default) {
      segmentArr[objIdx].overtimePrice = totalOverTimePrice
    } else {
      segmentArr[objIdx].overtimePrice = null;
    }
    if([1, true].includes(segment.fullBand)){  
      const tabsClone = [...tabsData];
      const tabIndex = tabsClone.findIndex((obj) => obj.id === "band");
      const changedRate = SegOp.getCustomRateForOvertimeSegment(
        segment,
        segment,
        tabsClone[tabIndex].price
      );
      segmentArr[objIdx].overtimePrice = changedRate;
    }
    setBandConfigData({
      ...bandConfigData,
      segments: segmentArr,
    });

    // coalesce the deferred recomputes: only the most recently scheduled one
    // may run, otherwise a timer from an earlier render can overwrite the
    // total computed for a later click (last-write-wins race)
    if (segmentTotalTimerRef.current) clearTimeout(segmentTotalTimerRef.current);
    segmentTotalTimerRef.current = setTimeout(() => {
      segmentTotalTimerRef.current = null;
      updateSegmentTotal(segmentArr);
    }, 1000);
  };

  const updateTravelTotal = async (travelData) => {
    const total = travelData.reduce((prev, cur) => {
      return prev + cur.marginRateWithCC;
    }, 0);

    let travelTotal = isNaN(total) ? 0 : Math.round(total);

    const updatedTabsData = await updateObject(tabsData, [
      { id: "travel", key: "price", value: travelTotal },
    ]);
    setTabsData([...updatedTabsData]);
  };

  const fetchFaqDetails = () => {
    AxiosInstance(4009, "application/json")
      .get(`/event/faq`)
      .then((resp) => {
        const response = resp?.data;
        if (
          response &&
          (response.status === 200 || response.status === "200")
        ) {
          const faqDetails = response.data[0]?.faq;
          const faqDataClone = { ...faqData };
          faqDetails.map((item) => (faqDataClone[item.key] = item.questions));
          setFaqData(faqDataClone);
        } else if (response?.status === 400 || response?.status === "400") {
          setFaqData({});
        }
      })
      .catch((err) => {
        var error = err && err.response;
        if (error && (error.status === "403" || error.status === 403)) {
          logout();
        } else if (error && (error.status === "500" || error.status === 500)) {
          console.log("Error", error.msg);
        }
      });
  };

  const _getSegmentOvertime = (segmentStartTime, segmentEndTime, segmentDuration) => {
    let startTime = moment(segmentStartTime, "hh:mm a");
    let endTime = moment(segmentEndTime, "hh:mm a");
    // calculating overtime
    let diff = endTime.diff(startTime, "minutes");

    if (startTime > endTime) {
      diff += 24 * 60;
    }
    diff /= 60;

    let overtime = diff - segmentDuration;

    if(overtime < 0) overtime = 0;
    return overtime;
  }

  const getFHTravelRateMargin = (travelArr=travelResponse, members, performers, marginValue = margin) => {

    const flightRate = performers * travelArr["flightFare"];
    const fightMarginRate = applyMargin(flightRate, marginValue);
    let flightMargin = fightMarginRate - flightRate;
    if (isNaN(flightMargin)) flightMargin = 0;

    // calc hotel margin price for 2 days
    const hotelRate = 2 * travelArr["hotelFare"] * members;
    const hotelMarginRate = applyMargin(hotelRate,marginValue);
    let hotelMargin = hotelMarginRate - hotelRate;
    if (isNaN(hotelMargin)) hotelMargin = 0;

    // sum of flight and hotel margin
    let price = flightMargin + hotelMargin;
    if (isNaN(price)) price = 0;

    return price;
  };

  const calculateCreditCardCharge = (amount, creditCardRate) => {
    let CCRate = (creditCardRate ? creditCardRate / 100 : creditCard != 0 ? creditCard : reduxState?.search?.creditCardRate / 100) ?? 3.5;
    let ccCharge = amount * 0.3 * CCRate
    return ccCharge
  }

  const calculateCommission = (amount=0, commissionPercent=commissionPercentage) => (amount * (commissionPercent ?? 0))/100

  const calculatePayrollTax = (amount) => ((amount ?? 0) * 0.1)

  const updateBandConfigData = async (response, travelData) => {
    const { team, segments, notes, haveSoundCompany } = response.result;
    const { totalCommission=0 } = response.commission ?? {}
    const marginValue = response?.margin === undefined ? 0 : response.margin;
    const creditCardPayload = response.creditCardCharge || reduxState?.search?.creditCardCharge

    setCommissionPercentage(totalCommission)

    let travelAllowance = response?.travelDetails?.data?.travelRate;
    if (isNaN(travelAllowance)) travelAllowance = travelData?.travelRate ?? 0;
    setTravelRate(travelAllowance);    

    //Initialize data structure
    const segmentsData = _initializeSegments(segments)
    const teamPayload = {
      teamClone: team,
      segmentsClone: segmentsData,
      marginValue,
      travelAllowance,
      creditCardPayload,
      commissionRate: totalCommission
    }
    const teamData = _initializeTeamAssignment(teamPayload)

    setBandConfigData({
      ...response.result,
      team: teamData,
      segments: segmentsData,
    });
    setMargin(parseFloat(marginValue));

    const extraData = await response.extra.map((item) => {
      let ccCharge = calculateCreditCardCharge(item.rate, creditCardPayload) ?? 0
      ccCharge = applyMargin(ccCharge, marginValue)
      let finalPrice = item.rate + ccCharge
      let commissionPrice = calculateCommission(finalPrice, totalCommission)
      finalPrice = Math.round(finalPrice + commissionPrice)
      return ({
        ...item,
        selected: item.default,
        rateWithCC: finalPrice
      })
    })?.sort((a, b) => a?.name?.localeCompare(b?.name, undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  );
    setExtraData(extraData);


    // get bands total
    const onlyBandTeam = team.filter((item) => [0,1,4,5,6].includes(item.type))
    const bandTotal = onlyBandTeam.reduce((sum, item) => sum + item.totalPrice, 0)

    // get strings total
    const onlyStringTeam = team.filter((item) => item?.type === 2)
    const stringsTotal = onlyStringTeam.reduce((sum, item) => sum + item.totalPrice, 0)

    // get extra total
    const extraTotal = extraData.reduce((prev, cur) => {
      if (cur.selected) return prev + parseInt(cur.rateWithCC);
      return prev;
    }, 0);

    const totalPerformers = await getTotalPerformers(team);
    const totalMembers = await getTotalMembers(team, haveSoundCompany);

    // get sum of margins of hotel and flight
    const FHTravelRate = getFHTravelRateMargin(travelData ?? travelResponse, totalMembers, totalPerformers, marginValue);

    let FHTravelRateWCC = FHTravelRate + (calculateCreditCardCharge(FHTravelRate, creditCardPayload) ?? 0)
    let FHTravelRateWVC = Math.round(FHTravelRateWCC + calculateCommission(FHTravelRateWCC, totalCommission))
    const bandRate = bandTotal + FHTravelRateWVC;

    // console.log({bandTotal, travelAllowance, TAwithMargin:  applyMargin(travelAllowance, marginValue), totalPerformers, FHTravelRate})

    let updatedTabsData = await updateObject(tabsData, [
      {
        id: "band",
        key: "price",
        value: bandRate,
      },
      {
        id: "strings",
        key: "price",
        value: stringsTotal,
      },
      {
        id: "extra",
        key: "price",
        value: extraTotal,
      },
    ]);


    // console.log({updatedTabsData})
    setCcAdded(false);
    setTabsData([...updatedTabsData]);
    setBandDataLoading(false);
    setBandDataError(false);

    if (notes !== null && notes !== undefined) {
      setNotes(notes);
    }
  };

  const _initializeSegments = (segmentsClone=[]) => {
    segmentsClone.forEach(segment => {
      segment.defaultMusicians =  segment.musicians
      segment.defaultPerformers = structuredClone(segment.performers || [])
      segment.overtimePrice = 0
    })
    return segmentsClone
  }

  const _initializeTeamAssignment = (payload={}) => {
    const {teamClone=[], segmentsClone=[], marginValue=0, travelAllowance=0, creditCardPayload=0, commissionRate} = payload

    teamClone.forEach((position) => _calculatePositionPrices({ position, segmentsClone, marginValue, travelAllowance, creditCardPayload, commissionRate}))

    return teamClone
  }

  const fetchProposalData = (proposalToken) => {
    AxiosInstance(4009, "application/json")
      .post(`/proposal`, { token: proposalToken })
      .then((resp) => {
        const response = resp && resp.data;
        if (
          response &&
          (response.status === 200 || response.status === "200")
        ) {
          fetchTravelDetails(response);
        } else if (response?.status === 400 || response?.status === "400") {
          toast.error(<ReactToastify title="Error" desc={response.msg} />);
          setBandConfigData([]);
          setExtraData([]);
          setBandDataLoading(false);
          setBandDataError(true);

          let searchMsg = "Unable to load proposal data";
          setBandDataError(searchMsg);
          setTravelErrorMsg(searchMsg);
          setTravelDataLoading(false);
          setDisableActions(true);
        }
      })
      .catch((err) => {
        var error = err && err.response;
        if (error && (error.status === "403" || error.status === 403)) {
          logout();
        } else if (error && (error.status === "500" || error.status === 500)) {
          console.log("Error", error.msg);
        }
      });
  };

  const fetchTravelDetails = (data, bandConfigData) => {

    const { latitude, longitude, event_place, event_location, date } = data;
    let url = `/travelPrices/${date}/${latitude}/${longitude}?event_place=${event_place??event_location}`;

    if(bandId){
      url += `/${bandId}`
    }

    AxiosInstance(4009, "application/json")
      .get(url)
      .then(async (resp) => {
        const response = resp && resp.data;
        if (
          response &&
          (response.status === 200 || response.status === "200")
        ) {
          const travelDetails = response.data;

          setTravelResponse(response.data);
          setTravelDistance(response.distance);
          dispatch(setTravelPriceAndDistance(response.data, response.distance));

          if (response.distance < 90) {
            let updatedTabsData = await updateObject(tabsData, [
              {
                id: "travel",
                key: "price",
                value: 0,
              },
            ]);
            setTabsData([...updatedTabsData]);
          }

          // hide strings section when event includes flight
          if (travelDetails.hasOwnProperty("flightFare")) {
            setStringsErrorMsg("Contact the team to add strings to your event");
          } else {
            setStringsErrorMsg("");
          }

          setTravelErrorMsg("");
          setTimeout(() => {
            setTravelDataLoading(false);
            updateBandConfigData(bandConfigData, response.data);
          }, 1000);
        }
        if (response?.status === 400 || response?.status === "400") {
          setTravelData([]);
          setTravelErrorMsg(response?.msg);
          setTravelDataLoading(false);
        }
      })
      .catch((err) => {
        var error = err && err.response;
        if (error && (error.status === "403" || error.status === 403)) {
          logout();
        } else if (error && (error.status === "500" || error.status === 500)) {
          console.log("Error", error.msg);
        }
      });
  };

  const fetchBandConfiguration = (id, searchQueryData) => {
    const { date, eventType, latitude, longitude, event_place,event_location } =
      searchQueryData;
    const shareLink = `${window.location.origin}/band/${slug}?eventType=${eventType}&date=${date}&event_place=${event_place??event_location}&latitude=${latitude}&longitude=${longitude}`;
    setShareUrl(shareLink);

    if (eventType !== undefined) {
      AxiosInstance(4009, "application/json")
        .get(
          `/band/${id}/configurations/${eventType}/${date}?latitude=${encodeURIComponent(latitude)}&longitude=${encodeURIComponent(longitude)}&event_place=${encodeURIComponent(event_place??event_location)}`
        )
        .then(async (resp) => {
          const response = resp && resp.data;
          if (
            response &&
            (response.status === 200 || response.status === "200")
          ) {
            setMargin(parseFloat(response.margin));
            setCreditCard(response.creditCardCharge ? response.creditCardCharge / 100 : reduxState?.search?.creditCardCharge / 100)
            setDiscount(response.discountPrice)
            setDiscounted(response.discountPrice)
            setDiscountAccess(response.discountAccess)
            fetchTravelDetails(searchQueryData, response);
          } else if (
            response &&
            (response.status === 400 || response.status === "400")
          ) {
            setBandConfigData([]);
            setExtraData([]);
            setBandDataLoading(false);
            setBandDataError(true);
          }
        })
        .catch((err) => {
          var error = err && err.response;
          if (error && (error.status === "403" || error.status === 403)) {
            logout();
          } else if (
            error &&
            (error.status === "500" || error.status === 500)
          ) {
            console.log("Error", error.msg);
          }
        });
    }
  };

  const handleNoteSave = (e) => {
    setNotes(e.target.value);
  };
  const handleDiscountValue = (event) => {
      const { value } = event.target;
      // Regular expression to match numbers or numbers with decimal points
      const isValidInput = /^-?\d*\.?\d*$/.test(value);
  
      if (isValidInput) {
        setDiscount(value);
      }    
  };
  const handleEventNoteSave = () => {
    if (!isAuthenticated) {
      onSaveClick(true);
    } else {
      const { date } = reduxState?.search?.searchData;
      AxiosInstance(4009, "application/json")
        .put(`/event/notes`, {
          bandId: bandId,
          eventDate: date,
          notes: notes,
        })
        .then(async (resp) => {
          const response = resp && resp.data;
          if (
            response &&
            (response.status === 200 || response.status === "200")
          ) {
            toast.success(
              <ReactToastify title="Success" desc="Notes saved successfully" />
            );
          } else if (
            response &&
            (response.status === 400 || response.status === "400")
          ) {
            toast.error(
              <ReactToastify title="Error" desc="Unable to save notes." />
            );
          }
        })
        .catch((err) => {
          var error = err && err.response;
          if (error && (error.status === "403" || error.status === 403)) {
            logout();
          } else if (
            error &&
            (error.status === "500" || error.status === 500)
          ) {
            console.log("Error", error.msg);
          }
        });
    }
  };
  const handleDiscountSave = () => {
    let discountedPrice = totalPrice !== 0 ? totalPrice - discount : totalDiscountPrice - discount ;
    setDiscounted(discount)
    setTotalDiscountPrice(discountedPrice)
    toast.success(
      <ReactToastify title="Success" desc="Discount Added" />
    );
  };
  const onSaveProposal = async (type) => {
    if (!isAuthenticated && type !== "share") {
      onSaveClick(true);
    } else {
      const { date, eventType, latitude, longitude, event_place,event_location} =
        reduxState?.search?.searchData;
      const { segments, team } = bandConfigData;

      console.log("onSaveProposal - bandConfigData:", {segments: segments?.length, team: team?.length, fullData: bandConfigData});

      // Guard: Check if segments and team exist
      if (!segments || !Array.isArray(segments) || !team || !Array.isArray(team)) {
        console.error("Save error: segments or team is missing!", { segments, team, bandConfigData });
        toast.error(<ReactToastify title="Error" desc="Configuration data is incomplete. Please refresh the page." />);
        setSavingData(false);
        return;
      }

      if (type === "save") {
        setSavingData(true);
      }

      // Preparing save request body
      const saveSegmentData = await segments.map((segment) => ({
        segmentId: segment._id,
        startTime: segment.startTime,
        endTime: segment.endTime,
        performersCount: segment.musicians,
        status: segment.default ? 1 : 0,
        performers: segment?.performers.map((item) => ({
          positionId: item.positionId,
          count: segment?.performers.filter(
            (performer) => performer.positionId === item.positionId
          ).length,
        })),
      }));

      const saveTeamData = await team.map((item) => ({
        positionId: item.positionId,
        count: item.default,
      }));

      const saveExtraData = extraData
        .filter((obj) => obj.selected === true)
        .map((item) => ({
          extraId: item._id,
        }));

      const data = {
        eventType: eventType,
        bandId: bandId,
        latitude: latitude,
        longitude: longitude,
        formatAddress: event_place ?? event_location,
        eventDate: date,
        name: eventType,
        team: saveTeamData,
        extra: saveExtraData,
        segments: saveSegmentData,
        type: type,
        shareToken: shareToken ?? null,
        discount: discount ? parseFloat(discount) : null,
      };

      AxiosInstance(4009, "application/json")
        .post(`/event`, data)
        .then(async (resp) => {
          const response = resp && resp.data;
          if (
            response &&
            (response.status === 201 || response.status === "201")
          ) {
            // if(discount){
            //   let data1 = {
            //     eventId: response.id,
            //     amount: parseFloat(discount),
            //   };
            //   try{
            //     const discountResp = await AxiosInstance(4009, "application/json").post(`/event/discount`, data1)
            //     const discResponse = discountResp?.data
            //   }catch(err){

            //   }              
            // }            
            if (type === "save") {
              toast.success(
                <ReactToastify title="Success" desc="Saved successfully" />
              );
            } else if (type === "book") {
              setBookNowLoading(false);
              window.location.href = `/contract/${response.id}`;
            } else if (type === "download") {
              // Downloads the file with real progress polling
              const proposalId = response.id;
              const url = ["production", "staging"].includes(process.env.REACT_APP_NODE_ENV)
                ? `${process.env.API_BASE_URL}/starlightband/api/v1/generateProposal/${response.id}`
                : `${process.env.API_BASE_URL}:4009/api/v1/generateProposal/${response.id}`;

              // Start polling backend for real generation progress
              startProgressPolling(response.id);

              const xhr = new XMLHttpRequest();
              xhr.open("GET", url, true);
              xhr.responseType = "blob";

              xhr.onload = () => {
                const contentType = xhr.getResponseHeader("Content-Type") || "";
                if (xhr.status === 200 && contentType.includes("application/pdf")) {
                  const blob = xhr.response;
                  const blobUrl = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = blobUrl;
                  a.download = `proposal-${proposalId}.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(blobUrl);
                  completeDownloadProgress();
                } else if (xhr.status === 403) {
                  toast.dismiss();
                  dispatch(onLogout());
                  logout();
                  resetDownloadProgress();
                  onSaveClick(true);
                } else {
                  toast.error(
                    <ReactToastify title="Error" desc="Failed to download proposal." />
                  );
                  resetDownloadProgress();
                }
              };

              xhr.onerror = () => {
                toast.error(
                  <ReactToastify title="Error" desc="Failed to download proposal." />
                );
                resetDownloadProgress();
              };

              xhr.send();
            }

          } else if (
            response &&
            (response.status === 400 || response.status === "400")
          ) {
            toast.error(
              <ReactToastify
                title="Error"
                desc="Unable to save your proposal."
              />
            );
            setShowSharePopup(false);
            setBookNowLoading(false);
            resetDownloadProgress();
          } else if (
            response &&
            (response.status === 500 || response.status === "500")
          ) {
            toast.error(<ReactToastify title="Error" desc={response.msg} />);
            setShowSharePopup(false);
            setBookNowLoading(false);
            resetDownloadProgress();
          }
        })
        .catch((err) => {
          var error = err && err.response;
          if (error && (error.status === "403" || error.status === 403)) {
            toast.dismiss()
            dispatch(onLogout());
            logout();
            resetDownloadProgress();
            return onSaveClick(true)
          } else if (
            error &&
            (error.status === "500" || error.status === 500)
          ) {
            setSavingData(false);
          } else {
            setSavingData(false);
            setShowSharePopup(false);
            setBookNowLoading(false);
            resetDownloadProgress();
          }
          toast.error(
            <ReactToastify title="Error" desc="Something went wrong!" />
          );
        }).finally(() =>  setSavingData(false));
    }
    if (typeof window !== 'undefined' && typeof window.gtag === 'function' && !["development"].includes(process.env.REACT_APP_NODE_ENV)) {
    window.gtag('event', 'Saveproposal', {
      'event_category': 'Proposals',
      'event_label': 'Save Proposal from Configure Band',
      'event_id': 'Goal ID 9 / Goal Set 2',
      'event_action': 'Saveproposal',
    });
  }
  };

  const onShareProposal = () => {
    setShowSharePopup(true);
    setShareLinkLoading(true);
    setTimeout(() => {
      setShareLinkLoading(false);
    }, 1000);
    // onSaveProposal("share");
  };

  const onDownloadProposal = () => {
    if (!isAuthenticated) {
      onSaveClick(true);
    } else {
      setDownloadLoading(true);
      setDownloadProgress(0);
      onSaveProposal("download");
    }
    if (typeof window !== 'undefined' && typeof window.gtag === 'function' && !["development"].includes(process.env.REACT_APP_NODE_ENV)) {
    window.gtag('event', 'Downloadproposal', {
      'event_category': 'Proposals',
      'event_label': 'Download Proposal from Configure Band',
      'event_id': 'Goal ID 8 / Goal Set 2',
      'event_action': 'Downloadproposal'
    });    }
  };

  const onBookProposal = () => {
    setBookNowLoading(true);
    //setting last index as completed
    if (completedTabs.indexOf(4) === -1) completedTabs.push(4);
    setCompletedTabs(completedTabs);

    if (!isAuthenticated) {
      onSaveClick(true);
      setBookNowLoading(false);
    } else {
      onSaveProposal("book");
    }
  };

  const onTabClick = (index) => {
    setActiveTabIndex(index);
    if (completedTabs.indexOf(index - 1) === -1) completedTabs.push(index - 1);
    setCompletedTabs(completedTabs);
  };

  /**
   * Update selected extra item and recalculate section total price
   * @param {new value} value Boolean
   * @param {object} item Object
   */
  const onExtraSelected = (value, item) => {
    const extraClone = [...extraData];
    const objIdx = extraClone.findIndex((obj) => obj._id === item._id);
    extraClone[objIdx].selected = value;

    // update extra count on select
    const tabsClone = [...tabsData];
    const tabIndex = tabsClone.findIndex((obj) => obj.id === "extra");
    setExtraData(extraClone);

    if (value) {
      const newTotal =
        tabsClone[tabIndex].price + extraClone[objIdx].rateWithCC;
      tabsClone[tabIndex].price = parseFloat(newTotal.toFixed(2));
    } else {
      const newTotal =
        tabsClone[tabIndex].price - extraClone[objIdx].rateWithCC;
      tabsClone[tabIndex].price = parseFloat(newTotal.toFixed(2));
    }
    setTabsData(tabsClone);
  };

  const handleSegmentSelect = (value, segment) => {
    const segmentClone = [...bandConfigData.segments];
    const objIdx = segmentClone.findIndex((obj) => obj._id === segment._id);
    segmentClone[objIdx].default = value;
    if (value === false) {
      segmentClone[objIdx].overtimePrice = 0;
    }
    calculateOverTime(segmentClone, objIdx, segment);
  };

  /**
   * Recalculate the overtime when segment timeframe change
   * @param {start / end time} key String
   * @param {new time} value String
   * @param {segment} segment Object
   */
  const onSegmentTimeChange = (key, value, segment) => {
    const segmentClone = [...bandConfigData.segments];
    const objIdx = segmentClone.findIndex((obj) => obj._id === segment._id);
    segmentClone[objIdx][key] = moment(value, "hh:mm a").format("hh:mm a");

    calculateOverTime(segmentClone, objIdx, segment);
  };

  const updateMusicianCount = (segments, type, add, bandData, targetedCorePerformerIdx = -1, updatedTeam) => {
    let updatedSegments = structuredClone(segments ?? []);
    const { positionId } = bandData;
    if ([0, 1].includes(type)) {
      updatedSegments = segments.map((item) => {
        const tPerformer = bandData?.performers?.[targetedCorePerformerIdx] || null;

        const defInx = !!tPerformer ? item?.defaultPerformers?.findIndex?.(
          p => !!p.performerId && p.performerId === tPerformer.performerId && p.positionId === positionId
        ) ?? -1 : -1;

        const segmentCorePerformerIdx = !!tPerformer ? item?.performers?.findIndex?.(
          p => p?.performerId && p?.performerId === tPerformer.performerId && p?.positionId === positionId
        ) : -1;

        const isSegmentUpdate = (add ? defInx > -1 : segmentCorePerformerIdx > -1) || !!item.fullBand;


        if (isSegmentUpdate) {
          if (add) {
            let newPerformer = { positionId, overtime: bandData.positionOvertime };
            if(defInx > -1){
              newPerformer = item?.defaultPerformers[defInx]
            }

            return {
              ...item,
              musicians: item.musicians + 1,
              performers: [
                ...item.performers,
                newPerformer,
              ],
            };
          } else {
            const performersClone = item.performers;
            let removeIndex = -1;
            if(segmentCorePerformerIdx > -1){
              removeIndex = segmentCorePerformerIdx;
            } else {
              removeIndex = performersClone.findIndex(p => p.positionId === positionId && !p.performerId);
            }

            if(removeIndex > -1){
              performersClone.splice(removeIndex, 1);
            }

            return {
              ...item,
              musicians: item.musicians - 1,
              performers: performersClone,
            };
          }          
        }
        return item;
      });
    } else if (type === 2) {
      // string musician total count
      let totalQty = updatedTeam
        .filter((obj) => obj?.type === 2)
        .reduce((prev, cur) => {
          return prev + cur.default;
        }, 0);

      console.log("Strings update - totalQty:", totalQty, "segments:", segments.map(s => ({name: s.name, isStringSpecial: s.isStringSpecial, musicians: s.musicians})));

      updatedSegments = segments.map((item) => {
        // Update segments that have isStringSpecial flag OR are explicitly string-related segments
        const isStringSegment = item.isStringSpecial ||
                                 (item.name && (item.name.toLowerCase().includes("ceremony") ||
                                                item.name.toLowerCase().includes("cocktail")));

        if (isStringSegment) {
          console.log(`Updating segment: ${item.name}, current musicians: ${item.musicians}, totalQty: ${totalQty}, add: ${add}`);
          if (add) {
            const performersCount = item.performers.length;
            return {
              ...item,
              musicians:
                totalQty === 0
                  ? item.defaultMusicians
                  : totalQty > 1
                    ? totalQty
                    : item.defaultMusicians + 1,
              performers:
                totalQty === 0
                  ? item.defaultPerformers
                  : (totalQty > 1
                    ? [
                      ...item.performers.slice(
                        performersCount - (totalQty - 1)
                      ),
                      {
                        positionId: positionId,
                        overtime: bandData.positionOvertime
                      },
                    ]
                    : [
                      ...item.performers,
                      {
                        positionId: positionId,
                        overtime: bandData.positionOvertime
                      },
                    ]),
            };
          } else {
            const performersClone = item.performers;
            for (var i = 0; i < performersClone.length; i++) {
              if (performersClone[i].positionId === positionId) {
                performersClone.splice(i, 1);
                break;
              }
            }
            return {
              ...item,
              musicians:
                totalQty === 0
                  ? item.defaultMusicians
                  : totalQty > 1
                  ? totalQty
                  : item.defaultMusicians + 1,
              performers:
                totalQty === 0
                  ? item.defaultPerformers
                  : totalQty > 1
                  ? performersClone
                  : [
                      ...item.defaultPerformers,
                      {
                        positionId: positionId,
                        overtime: bandData.positionOvertime
                      },
                    ],
            };
          }
        }
        return item;
      });
    } else if (type === 5) {
      updatedSegments = segments.map((item) => {
        if(item.isStringAndMusicianSpecial){

          const tPerformer = bandData?.performers?.[targetedCorePerformerIdx] || null;

          const defInx = !!tPerformer ? item?.defaultPerformers?.findIndex?.(
            p => !!p.performerId && p.performerId === tPerformer.performerId && p.positionId === positionId
          ) ?? -1 : -1;
  
          const segmentCorePerformerIdx = !!tPerformer ? item?.performers?.findIndex?.(
            p => p?.performerId && p?.performerId === tPerformer.performerId && p?.positionId === positionId
          ) : -1;

          if (add) {
            let newPerformer = { positionId, overtime: bandData.positionOvertime };
            if(defInx > -1){
              newPerformer = item?.defaultPerformers[defInx]
            }
            console.log("Newly added electric performer", newPerformer)

            return {
              ...item,
              musicians: item.musicians + 1,
              performers: [...item.performers, newPerformer]
            }
          } else {
            const performersClone = item.performers;
            let removeIndex = -1;
            if(segmentCorePerformerIdx > -1){
              removeIndex = segmentCorePerformerIdx;
            } else {
              removeIndex = performersClone.findIndex(p => p.positionId === positionId && !p.performerId);
            }

            if(removeIndex > -1){
              performersClone.splice(removeIndex, 1);
            }
            
            console.log("Removed electric g", {removeIndex, performersClone})

            return { ...item, musicians: item.musicians - 1, performers: performersClone }
          }
        }
        return item;
      });
    }
    return updatedSegments;
  };

  const _calculatePositionPrices = (positionProps={}) => {
    
    const { 
      position = {},
      segmentsClone = [],
      marginValue = margin,
      travelAllowance = travelRate,
      creditCardPayload,
      commissionRate = commissionPercentage } = positionProps;

    let totalPrice = 0
    let totalPayrollTax = 0

    // remove travel allowance for strings and noTravel positions. Here type: 2 is string
    let positionTravelAllowance = ((position.noTravel || [2].includes(position.type)) ? 0 : travelAllowance) ?? 0

    const segmentIds = segmentsClone.map(s => s._id);
    const fullBandSegmentIds = segmentsClone.filter(s => s.fullBand).map(s => s._id);
    const stringSegmentIds = segmentsClone.filter(s => s.isStringSpecial).map(s => s._id);
    const stringNMusicianSegmentIds = segmentsClone.filter(s => s.isStringAndMusicianSpecial).map(s => s._id);

    // function getSegmentAdditional(segmentDetail, position, performer) {
    //   const segAdd = 0
    //   if (segmentDetail) {
    //     //calculate segment additional only for for non-full band (excluding soundcompany)
    //     if (position.type !== 6 && !segmentDetail.fullBand) segAdd += (performer?.overtime || 100)

    //     //calculate segment overtime rate for activeSegment --- currently calculated in segments
    //     // if(segmentDetail.default){
    //     //   const overtime = _getSegmentOvertime(segmentDetail.startTime, segmentDetail.endTime, segmentDetail.duration) || 0
    //     //   segmentOvertimePrice += (overtime * (performer?.overtime || position.positionOvertime || 0))
    //     // }
    //   }

    //   return segAdd
    // }

    function calculatePerformerSegmentAdditional(position, performer) {
      let segmentAdditionalPrice = 0
      let chargeableSegments = []
      if (position.type !== 6) {
        if (position.type === 2) { //string
          chargeableSegments = segmentIds?.filter(sId => stringSegmentIds.includes(sId) && !fullBandSegmentIds.includes(sId));
        } else if (position.type === 5) { //string & musician (electric guitar)
          chargeableSegments = segmentIds?.filter(sId => stringNMusicianSegmentIds.includes(sId) && !fullBandSegmentIds.includes(sId));
        } else {
          chargeableSegments = performer?.assignableSegments?.filter(sObj => !fullBandSegmentIds.includes(sObj.id))?.map(sObj => sObj.id) || [];
        }
        chargeableSegments?.forEach(chargeableSegmentId => {
          let theRate = performer?.segmentRates?.find(s => s.segmentId === chargeableSegmentId)?.rate ?? (performer?.overtime || 100)
          segmentAdditionalPrice += theRate;
        })

        // segmentAdditionalPrice = chargeableSegments.length * (performer?.overtime || 100)
      }
      return segmentAdditionalPrice
    }

    if(position.default > 0){
      for (let posNo = 0; posNo < position.default; posNo++) {
        let performer = position.performers?.[posNo] ?? {}

        let segmentAdditional = calculatePerformerSegmentAdditional(position, performer);




        // segmentsClone.forEach(segmentDetail => {
        //   if (Array.isArray(segmentDetail.performers)) {
        //     for (let segmentPerformer of segmentDetail.performers) {
        //       if (segmentPerformer.positionId === position.positionId) {
        //         if (!segmentPerformer.performerId && !performer.performerId) {
        //           segmentAdditional += getSegmentAdditional(segmentDetail, position, performer)
        //           return;
        //         }
        //         else if (segmentPerformer.performerId === performer.performerId) {
        //           segmentAdditional += getSegmentAdditional(segmentDetail, position, performer)
        //         }
        //       }
        //     }
        //   }
        // })

        let performerRate = performer.rate ?? position.positionRate ?? 0

        let performerRateWM = applyMargin(performerRate, marginValue)
        let segmentAdditionalWM = applyMargin(segmentAdditional, marginValue)
        // let segmentOvertimeWM = applyMargin(segmentOvertimePrice, marginValue)
        let positionTravelAllowanceWM = applyMargin(positionTravelAllowance, marginValue)

        //10% of total payroll of a performer 
        let payrollTaxPriceWM = (performer.performerId && performer.w2Employee) ?
          calculatePayrollTax(performerRateWM + segmentAdditionalWM + /*segmentOvertimeWM + */ positionTravelAllowanceWM)
          : 0
        totalPayrollTax += payrollTaxPriceWM

        let performerTotalPriceWithMargin = performerRateWM + segmentAdditionalWM + payrollTaxPriceWM + positionTravelAllowanceWM
        let ccCharge = calculateCreditCardCharge(performerTotalPriceWithMargin, creditCardPayload)

        performerTotalPriceWithMargin += (ccCharge ?? 0)
        let venueCommisionPrice = calculateCommission(performerTotalPriceWithMargin, commissionRate)

        performerTotalPriceWithMargin = Math.round(performerTotalPriceWithMargin + venueCommisionPrice)
        totalPrice += performerTotalPriceWithMargin

        // console.table({ positionN:position.name, performerRate, sgA: segmentAdditionalWM, rateWM:performerRateWM, payrollTaxWM: payrollTaxPriceWM, cc:ccCharge, venueCommisionPrice, total: finalPositionPrice  })
      }
    }

    position.totalPayrollTax = totalPayrollTax
    position.totalPrice = totalPrice
    position.marginRate = Math.round(applyMargin(position.rate, marginValue))

    // console.log({ positionN:position.name, totalPrice })

    return position
  }

  /**
   * Update quantity and recalculate total price of section
   * @param {new quantity} value int
   * @param {object id} id String
   * @param {section name} object String
   */
  const onQtyChange = (value, id, object, bandType) => {
    const segmentClone =  structuredClone(bandConfigData.segments ?? []);
    const teamClone = structuredClone(bandConfigData.team ?? []);
    const tabsClone = [...tabsData];
    const isBand = object === "musicians" || object === "vocalists";
    let updatedSegmentClone = [];

    if (isBand) {
      const objIdx = teamClone.findIndex((obj) => obj.positionId === id);
      const oldValue = teamClone[objIdx].default;

      const positionOldData = _calculatePositionPrices({position: bandConfigData.team[objIdx], segmentsClone: bandConfigData.segments})

      teamClone[objIdx].default = value;

      const isAdd = oldValue < value;
      const corePerformers = teamClone[objIdx].performers?.filter?.((p) => !!p?.performerId) || [];

      let targetedCorePerformerIdx = -1;
      if (!!corePerformers.length) {
        if (isAdd && value <= corePerformers.length) {
          targetedCorePerformerIdx = value - 1;
        } else if (!isAdd && oldValue <= corePerformers.length) {
          targetedCorePerformerIdx = value;
        }
      }

      updatedSegmentClone = updateMusicianCount(
        segmentClone,
        bandType,
        isAdd,
        teamClone[objIdx],
        targetedCorePerformerIdx,
        teamClone
      );

      const positionNewData = _calculatePositionPrices({ position: teamClone[objIdx], segmentsClone: updatedSegmentClone})

      const modifiedPrice = (positionNewData?.totalPrice - positionOldData?.totalPrice)
      const bandTabIndex = tabsClone.findIndex((obj) => obj.id === "band");
      tabsClone[bandTabIndex].price += modifiedPrice;

    } else if (object === "strings") {
      const objIdx = teamClone.findIndex((obj) => obj.positionId === id);
      const oldValue = teamClone[objIdx].default;

      //old payrollTax and segment Additional
      const positionOldData = _calculatePositionPrices({position: bandConfigData.team[objIdx],  segmentsClone: bandConfigData.segments})

      teamClone[objIdx].default = value;
      const isAdd = oldValue < value;

      //Update total price in tabs section
      const tabIndex = tabsClone.findIndex((obj) => obj.id === "strings");

      updatedSegmentClone = updateMusicianCount(
        segmentClone,
        bandType,
        isAdd,
        teamClone[objIdx],
        -1,
        teamClone
      );

      //payrollTax and segment Additional
      const positionNewData = _calculatePositionPrices({ position: teamClone[objIdx],  segmentsClone: updatedSegmentClone})

      const modifiedPrice = (positionNewData?.totalPrice - positionOldData?.totalPrice)
      tabsClone[tabIndex].price += modifiedPrice;

    }
    setBandConfigData({
      ...bandConfigData,
      team: teamClone,
      segments: updatedSegmentClone,
    });
    setTabsData([...tabsClone]);
  };

  const sharePopup = (
    <div className="result_share_popup">
      {shareLinkLoading === false ? (
        <>
          <EmailShareButton url={shareUrl} subject="Starlight Music">
            <Tooltip title="Email">
              <EmailIcon
                round
                style={{ width: "34px", height: "34px" }}
                onClick={() => setShowSharePopup(false)}
              />
            </Tooltip>
          </EmailShareButton>
          <Tooltip title="Copy Link">
            <button
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                toast.success(
                  <ReactToastify title="Success" desc="Copied to clipboard" />
                );
                setShowSharePopup(false);
              }}
              className="img_btn propsal_share"
            >
              <img src={"/assets/images/copy_icon.svg"} alt="copy_icon" />
            </button>
          </Tooltip>
          <WhatsappShareButton
            url={shareUrl}
            onClick={() => setShowSharePopup(false)}
          >
            <Tooltip title="WhatsApp">
              <WhatsappIcon round style={{ width: "34px", height: "34px" }} />
            </Tooltip>
          </WhatsappShareButton>
        </>
      ) : (
        <LoadingOutlined style={{ fontSize: 24 }} spin />
      )}
    </div>
  );

  const { segments, team } = bandConfigData;

  return (
    <section id="band-configuration" className={styles.band_configuration}>
      <div className={styles.band_conf_pink_bg_full}>
        <div className={styles.band_conf_pink_bg}>
          <div className={styles.band_conf_leftside_pink_bg}></div>
          <div className={styles.band_conf_leftside_pink_bg1}></div>
          <div className={styles.band_conf_rightside_pink_bg}></div>
          <div className={styles.band_conf_rightside_pink_bg1}></div>
          <div className={styles.band_conf_rightside_pink_bg2}></div>
        </div>
      </div>
      <div className={styles.bandConfig_container}>
        <h1 className={styles.title}>
          {cmsData?.pricing_heading !== null
            ? cmsData?.pricing_heading
            : "Pricing"}
        </h1>
        <div className={styles.tab_header_sticky}>
          <ul className={styles.tab_links} role="tablist">
            {tabsData.map((tab, index) => (
              <motion.li
                key={tab.id}
                className={`${styles.tab} ${
                  activeTabIndex === index ? styles.active : ""
                }`}
                role="presentation"
              >
                <a onClick={() => onTabClick(index)}>
                  {tab.icon}
                  {/* <img src={tab.icon} alt={tab.title} /> */}
                  <div className={styles.tab_content}>
                    <motion.span>${formatToCurrency(tab.price)}</motion.span>
                    <motion.span>{tab.title}</motion.span>
                  </div>
                </a>
              </motion.li>
            ))}
            <div
              key="price"
              className={`${styles.tab} ${styles.price_tab} ${
                activeTabIndex === 4 ? styles.last_step : ""
              }`}
            >
              <motion.span>Total Price</motion.span>
              <motion.span className={styles.total}>${totalDiscountPrice ? formatToCurrency(totalDiscountPrice) : formatToCurrency(totalPrice)}</motion.span>
            </div>
          </ul>
        </div>
        <div
          key="price"
          className={`${styles.tab} ${styles.price_tab_total_mobile} ${
            activeTabIndex === 4 ? styles.last_step : ""
          }`}
        >
          <motion.span>Total Price</motion.span>
          <motion.span className={styles.total} id="ttl-price-text">${totalDiscountPrice ? formatToCurrency(totalDiscountPrice) : formatToCurrency(totalPrice)}</motion.span>
        </div>
        <div className={styles.bandConfig_body}>
          <ul className={styles.side_tab_links} role="tablist">
            {tabsData.map((tab, index) => (
              <motion.li
                key={tab.id}
                className={`${styles.side_tab} 
                ${activeTabIndex === index ? styles.active : styles.inactive}
                ${activeTabIndex > index ? styles.complete : styles.incomplete}
                `}
                role="presentation"
              >
                <a onClick={() => onTabClick(index)}>{tab.icon}</a>
                <motion.span>{index + 1}</motion.span>
              </motion.li>
            ))}
          </ul>
          <div className={styles.tab_content_wrapper}>
            <TabContent
              key="segment"
              data={tabsData[0]}
              tabsCount={tabsData.length}
              id="segment-content"
              onTabClick={onTabClick}
              activeTabIndex={activeTabIndex}
              active={activeTabIndex === 0}
              error={bandDataError}
              eventType={reduxState.search?.searchData?.eventType}
              faqData={faqData?.segment}
              onBookNow={onBookProposal}
              bookNowLoading={bookNowLoading}
              completedTabs={completedTabs}
              notes={notes}
              saveNote={handleNoteSave}
              saveDiscount={handleDiscountValue}
              discount={discount}
              discountAccess={discountAccess}
              saveEventNotes={handleEventNoteSave}
              saveDiscountValue={handleDiscountSave}
              disableAction={
                bandDataLoading ||
                !!!reduxState.search?.searchData ||
                disableActions
              }
            >
              {!!segments && (
                <SegmentComponent
                  data={segments}
                  loading={bandDataLoading}
                  eventType={reduxState.search?.searchData?.eventType}
                  onSegmentSelect={handleSegmentSelect}
                  handleSegmentTimeChange={onSegmentTimeChange}
                  cmsData={cmsData}
                />
              )}
            </TabContent>
            <TabContent
              key="band"
              data={tabsData[1]}
              tabsCount={tabsData.length}
              id="band-content"
              onTabClick={onTabClick}
              activeTabIndex={activeTabIndex}
              active={activeTabIndex === 1}
              error={bandDataError}
              faqData={faqData?.band}
              onBookNow={onBookProposal}
              bookNowLoading={bookNowLoading}
              completedTabs={completedTabs}
              notes={notes}
              saveNote={handleNoteSave}
              saveDiscount={handleDiscountValue}
              discount={discount}
              discountAccess={discountAccess}
              saveEventNotes={handleEventNoteSave}
              saveDiscountValue={handleDiscountSave}
              disableAction={
                bandDataLoading ||
                !!!reduxState.search?.searchData ||
                disableActions
              }
            >
              <BandComponent data={team} onQtyChange={onQtyChange} />
            </TabContent>
            <TabContent
              key="strings"
              data={tabsData[2]}
              tabsCount={tabsData.length}
              id="strings-content"
              onTabClick={onTabClick}
              activeTabIndex={activeTabIndex}
              active={activeTabIndex === 2}
              error={bandDataError}
              faqData={faqData?.strings}
              onBookNow={onBookProposal}
              bookNowLoading={bookNowLoading}
              completedTabs={completedTabs}
              notes={notes}
              saveNote={handleNoteSave}
              saveDiscount={handleDiscountValue}
              discount={discount}
              discountAccess={discountAccess}
              saveEventNotes={handleEventNoteSave}
              saveDiscountValue={handleDiscountSave}
              disableAction={
                bandDataLoading ||
                !!!reduxState.search?.searchData ||
                disableActions
              }
            >
              <StringsComponent
                data={team}
                onQtyChange={onQtyChange}
                marginRate={margin}
                errorMsg={stringsErrorMsg}
              />
            </TabContent>
            <TabContent
              key="travel"
              data={tabsData[3]}
              tabsCount={tabsData.length}
              id="travel-content"
              onTabClick={onTabClick}
              activeTabIndex={activeTabIndex}
              active={activeTabIndex === 3}
              faqData={faqData?.travel}
              onBookNow={onBookProposal}
              bookNowLoading={bookNowLoading}
              completedTabs={completedTabs}
              notes={notes}
              saveNote={handleNoteSave}
              saveDiscount={handleDiscountValue}
              discount={discount}
              discountAccess={discountAccess}
              saveEventNotes={handleEventNoteSave}
              saveDiscountValue={handleDiscountSave}
              disableAction={
                bandDataLoading ||
                !!!reduxState.search?.searchData ||
                disableActions
              }
            >
              <TravelComponent
                data={travelData}
                rawData={travelResponse}
                errorMsg={travelErrorMsg}
                loading={travelDataLoading}
                teamData={team}
                margin={margin}
                distance={travelDistance}
                calculateCreditCardCharge={calculateCreditCardCharge}
                calculateCommission={calculateCommission}
                updateTravelTotal={updateTravelTotal}
                haveSoundCompany={bandConfigData.haveSoundCompany}
              />
            </TabContent>
            <TabContent
              key="extra"
              data={tabsData[4]}
              tabsCount={tabsData.length}
              id="extras-content"
              onTabClick={onTabClick}
              activeTabIndex={activeTabIndex}
              active={activeTabIndex === 4}
              error={bandDataError}
              faqData={faqData?.extras}
              onBookNow={onBookProposal}
              bookNowLoading={bookNowLoading}
              completedTabs={completedTabs}
              notes={notes}
              saveNote={handleNoteSave}
              saveDiscount={handleDiscountValue}
              discount={discount}
              discountAccess={discountAccess}
              saveEventNotes={handleEventNoteSave}
              saveDiscountValue={handleDiscountSave}
              disableAction={
                bandDataLoading ||
                !!!reduxState.search?.searchData ||
                disableActions
              }
            >
              <ExtraComponent
                data={reduxState.search?.searchData?.eventType?.toLowerCase() === "wedding" ? extraData : extraData?.filter(e => !e.name.toLowerCase().includes("ceremony"))}
                onExtraSelected={onExtraSelected}
              />
            </TabContent>
          </div>
        </div>
        {reduxState.search?.searchData?.eventType?.toLowerCase() === "wedding" ? <div
          className={`d-flex ${styles.bandConfig_card_footer_note} ${styles.mob_bandConfig_card_footer_note}`}
        >
          {activeTabIndex === 1 && (
            <>
              <p style={{ minWidth: "fit-content", paddingLeft: "12px" }}>
                Note <span>*</span>
              </p>
              <p>
                {cmsData?.pricing_band_instruction !== undefined ? (
                  <Interweave
                    allowElements={false}
                    content={cmsData?.pricing_band_instruction}
                  />
                ) : (
                  "Pricing shown, includes the band, for up to a 4-hour Reception. Electric Violinists will also be added to Ceremony"
                )}
              </p>
            </>
          )}
          {activeTabIndex === 2 && (
            <>
              <p style={{ minWidth: "fit-content", paddingLeft: "12px" }}>
                Note <span>*</span>
              </p>
              <p>
                {cmsData?.pricing_string_instruction !== undefined ? (
                  <Interweave
                    allowElements={false}
                    content={cmsData?.pricing_string_instruction}
                  />
                ) : (
                  "Any two string players or more, are included for up to 2 hours and will perform at Ceremony and Cocktail. Contact us if you prefer only the band musicians for Cocktail Hour."
                )}
              </p>
            </>
          )}
          {activeTabIndex === 3 && (
            <>
              <p style={{ minWidth: "fit-content", paddingLeft: "12px" }}>
                Note <span>*</span>
              </p>
              <p>
                {cmsData?.pricing_travel_instruction !== undefined ? (
                  <Interweave
                    allowElements={false}
                    content={cmsData?.pricing_travel_instruction}
                  />
                ) : (
                  "Any two string players or more, are included for up to 2 hours and will perform at Ceremony and Cocktail. Contact us if you prefer only the band musicians for Cocktail Hour."
                )}
              </p>
            </>
          )}
          {activeTabIndex === 4 && (
            <>
              <p style={{ minWidth: "fit-content", paddingLeft: "12px" }}>
                Note <span>*</span>
              </p>
              <p>
                {cmsData?.pricing_extra_instruction !== undefined ? (
                  <Interweave
                    allowElements={false}
                    content={cmsData?.pricing_extra_instruction}
                  />
                ) : (
                  "Any two string players or more, are included for up to 2 hours and will perform at Ceremony and Cocktail. Contact us if you prefer only the band musicians for Cocktail Hour."
                )}
              </p>
            </>
          )}
        </div>: <div></div>}
        <div className={`d-flex align-items-center justify-content-between`}>
          {reduxState.search?.searchData?.eventType?.toLowerCase() === "wedding" ? <div
            className={`d-flex ${styles.bandConfig_card_footer_note} ${styles.bandConfigpage_card_footer_note}`}
          >
            {activeTabIndex === 1 && (
              <>
                <p style={{ minWidth: "fit-content", paddingLeft: "12px" }}>
                  Note <span>*</span>
                </p>
                <p>
                  {cmsData?.pricing_band_instruction !== undefined ? (
                    <Interweave
                      allowElements={false}
                      content={cmsData?.pricing_band_instruction}
                    />
                  ) : (
                    "Pricing shown, includes the band, for up to a 4-hour Reception. Electric Violinists will also be added to Ceremony"
                  )}
                </p>
              </>
            )}
            {activeTabIndex === 2 && (
              <>
                <p style={{ minWidth: "fit-content", paddingLeft: "12px" }}>
                  Note <span>*</span>
                </p>
                <p>
                  {cmsData?.pricing_string_instruction !== undefined ? (
                    <Interweave
                      allowElements={false}
                      content={cmsData?.pricing_string_instruction}
                    />
                  ) : (
                    "Any two string players or more, are included for up to 2 hours and will perform at Ceremony and Cocktail. Contact us if you prefer only the band musicians for Cocktail Hour."
                  )}
                </p>
              </>
            )}
            {activeTabIndex === 3 && (
              <>
                <p style={{ minWidth: "fit-content", paddingLeft: "12px" }}>
                  Note <span>*</span>
                </p>
                <p>
                  {cmsData?.pricing_travel_instruction !== undefined ? (
                    <Interweave
                      allowElements={false}
                      content={cmsData?.pricing_travel_instruction}
                    />
                  ) : (
                    "Any two string players or more, are included for up to 2 hours and will perform at Ceremony and Cocktail. Contact us if you prefer only the band musicians for Cocktail Hour."
                  )}
                </p>
              </>
            )}
            {activeTabIndex === 4 && (
              <>
                <p style={{ minWidth: "fit-content", paddingLeft: "12px" }}>
                  Note <span>*</span>
                </p>
                <p>
                  {cmsData?.pricing_extra_instruction !== undefined ? (
                    <Interweave
                      allowElements={false}
                      content={cmsData?.pricing_extra_instruction}
                    />
                  ) : (
                    "Any two string players or more, are included for up to 2 hours and will perform at Ceremony and Cocktail. Contact us if you prefer only the band musicians for Cocktail Hour."
                  )}
                </p>
              </>
            )}
          </div> : <div></div>}
          <div
            className={styles.bandConfig_card_footer}
            disabled={
              bandDataLoading ||
              !!!reduxState.search?.searchData ||
              disableActions
            }
          >
            <Popover
              content={sharePopup}
              visible={showSharePopup && !bandDataLoading}
              onVisibleChange={(visible) => setShowSharePopup(visible)}
              trigger="click"
              className={styles.footer_btn_light}
              onClick={() => onShareProposal()}
            >
              <img
                className={styles.btn_icons}
                src={"/assets/icons/share-icon.svg"}
                alt="share"
              />
              <span className={styles.share_icon} id="share-prop-link">Share</span>
            </Popover>
            <button
              id="dl-prop-btn"
              className={styles.footer_btn_solid}
              onClick={() => onDownloadProposal()}
              disabled={downloadLoading}
            >
              <span
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    opacity: downloadLoading ? 0 : 1,
                    transform: downloadLoading ? "translateY(8px)" : "translateY(0)",
                    transition: "opacity 0.6s ease, transform 0.6s ease",
                  }}
                >
                  <img
                    className={styles.btn_icon}
                    src={"/assets/icons/download-tray-icon.svg"}
                    alt="download"
                  />
                  Download Proposal
                </span>
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    opacity: downloadLoading ? 1 : 0,
                    transform: downloadLoading ? "translateY(0)" : "translateY(-8px)",
                    transition: "opacity 0.6s ease, transform 0.6s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  <CircularProgress
                    progress={downloadProgress}
                    loading={downloadLoading}
                    size={28}
                  />
                  Preparing...
                </span>
              </span>
            </button>
            <button
              id="save-prop-btn"
              className={styles.footer_btn_solid}
              onClick={() => onSaveProposal("save")}
            >
              {savingData && (
                <LoadingOutlined
                  style={{ fontSize: 24, marginRight: 10 }}
                  spin
                />
              )}
              <span>Save</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BandConfiguration;
